<div class="catalog-page">

  <!-- ── Page header ── -->
  <div class="page-header">
    <div class="page-title-row">
      <mat-icon class="page-icon">smart_toy</mat-icon>
      <div>
        <h1 class="page-title">Agent Intelligence Center</h1>
        <p class="page-sub">Review the knowledge, guidance, and connections of every agent in the AlreadyDone.us pipeline</p>
      </div>
    </div>
    <div class="tab-switcher">
      <button mat-stroked-button [class.tab-active]="activeTab === 'catalog'" (click)="activeTab = 'catalog'">
        <mat-icon>menu_book</mat-icon> Agent Catalog
      </button>
      <button mat-stroked-button [class.tab-active]="activeTab === 'graph'" (click)="activeTab = 'graph'">
        <mat-icon>account_tree</mat-icon> Connection Graph
      </button>
    </div>
  </div>

  <!-- ══════════════════════════════════════════════════════════════ -->
  <!--  TAB 1 — AGENT CATALOG                                        -->
  <!-- ══════════════════════════════════════════════════════════════ -->
  <div class="catalog-layout" *ngIf="activeTab === 'catalog'">

    <!-- Left panel: agent list -->
    <div class="agent-list-panel">
      <div class="search-box">
        <mat-icon class="search-icon">search</mat-icon>
        <input class="search-input" placeholder="Search agents, skills, domains…"
               [(ngModel)]="searchQuery"
               (ngModelChange)="onSearch($event)">
      </div>

      <div *ngIf="loading" class="list-loading">
        <mat-spinner diameter="28"></mat-spinner>
        <span>Loading agents…</span>
      </div>

      <!-- Layer 0 -->
      <div class="layer-group" *ngIf="!loading">
        <div class="layer-label" style="border-left-color: #7C3AED">Orchestrator</div>
        <ng-container *ngFor="let a of filtered">
          <div class="agent-row" *ngIf="a.layer === 0"
               [class.agent-row-active]="selected?.name === a.name"
               (click)="select(a)">
            <mat-icon class="agent-row-icon" [style.color]="a.color">{{a.icon}}</mat-icon>
            <div class="agent-row-text">
              <span class="agent-row-name">{{a.name}}</span>
              <span class="agent-row-role">{{a.role}}</span>
            </div>
          </div>
        </ng-container>
      </div>

      <!-- Layer 1 -->
      <div class="layer-group" *ngIf="!loading">
        <div class="layer-label" style="border-left-color: #0284C7">Specialist Agents</div>
        <ng-container *ngFor="let a of filtered">
          <div class="agent-row" *ngIf="a.layer === 1"
               [class.agent-row-active]="selected?.name === a.name"
               (click)="select(a)">
            <mat-icon class="agent-row-icon" [style.color]="a.color">{{a.icon}}</mat-icon>
            <div class="agent-row-text">
              <span class="agent-row-name">{{a.name}}</span>
              <span class="agent-row-role">{{a.role}}</span>
            </div>
            <span class="sub-count" *ngIf="a.subAgents?.length">{{a.subAgents.length}} sub</span>
          </div>
        </ng-container>
      </div>

      <div class="empty-list" *ngIf="!loading && filtered.length === 0">
        No agents match "{{searchQuery}}"
      </div>
    </div>

    <!-- Right panel: agent detail -->
    <div class="agent-detail-panel" *ngIf="selected">

      <!-- Agent hero -->
      <div class="agent-hero" [style.border-left-color]="selected.color">
        <div class="agent-hero-icon" [style.background]="selected.color + '22'" [style.color]="selected.color">
          <mat-icon>{{selected.icon}}</mat-icon>
        </div>
        <div class="agent-hero-info">
          <div class="agent-hero-name">{{selected.name}}</div>
          <div class="agent-hero-role">{{selected.role}}</div>
          <span class="layer-badge" [style.background]="layerColor(selected.layer)">
            Layer {{selected.layer}} — {{layerLabel(selected.layer)}}
          </span>
        </div>
      </div>

      <p class="agent-description">{{selected.description}}</p>

      <mat-tab-group animationDuration="150ms" class="detail-tabs">

        <!-- Overview tab -->
        <mat-tab label="Overview">
          <div class="tab-content">
            <div class="detail-section">
              <h3 class="section-title"><mat-icon>task_alt</mat-icon> Responsibilities</h3>
              <ul class="bullet-list">
                <li *ngFor="let r of selected.responsibilities">{{r}}</li>
              </ul>
            </div>
            <div class="io-row">
              <div class="io-card">
                <div class="io-label"><mat-icon>input</mat-icon> Inputs</div>
                <div class="io-value">{{selected.inputs}}</div>
              </div>
              <div class="io-card">
                <div class="io-label"><mat-icon>output</mat-icon> Outputs</div>
                <div class="io-value">{{selected.outputs}}</div>
              </div>
            </div>
          </div>
        </mat-tab>

        <!-- Knowledge tab -->
        <mat-tab label="Knowledge Domains">
          <div class="tab-content">
            <div class="domain-chips">
              <span class="domain-chip" *ngFor="let d of selected.knowledgeDomains"
                    [style.border-color]="selected.color" [style.color]="selected.color">
                {{d}}
              </span>
            </div>
          </div>
        </mat-tab>

        <!-- Sub-agents tab -->
        <mat-tab label="Sub-Agents" *ngIf="selected.subAgents?.length">
          <div class="tab-content">
            <div class="sub-agent-card" *ngFor="let s of selected.subAgents">
              <div class="sub-agent-name">
                <mat-icon [style.color]="selected.color">arrow_right</mat-icon>
                {{s.name}}
              </div>
              <div class="sub-agent-desc">{{s.description}}</div>
            </div>
            <div class="sub-note" *ngIf="!selected.subAgents?.length">
              This agent has no Layer 2 sub-agents — it runs its logic directly.
            </div>
          </div>
        </mat-tab>

        <!-- Guidance tab -->
        <mat-tab label="Guidance">
          <div class="tab-content">
            <div class="guidance-item" *ngFor="let g of selected.guidance; let i = index">
              <div class="guidance-num" [style.background]="selected.color">{{i + 1}}</div>
              <div class="guidance-text">{{g}}</div>
            </div>
          </div>
        </mat-tab>

      </mat-tab-group>
    </div>

    <div class="detail-empty" *ngIf="!selected && !loading">
      <mat-icon>smart_toy</mat-icon>
      <p>Select an agent from the list to view its full knowledge profile.</p>
    </div>
  </div>

  <!-- ══════════════════════════════════════════════════════════════ -->
  <!--  TAB 2 — CONNECTION GRAPH                                     -->
  <!-- ══════════════════════════════════════════════════════════════ -->
  <div class="graph-tab" *ngIf="activeTab === 'graph'">

    <div class="graph-legend">
      <span class="legend-item"><span class="legend-dot" style="background:#1E40AF"></span> Pipeline Flow</span>
      <span class="legend-item"><span class="legend-dot" style="background:#7C3AED"></span> LLM Provider</span>
      <span class="legend-item"><span class="legend-dot" style="background:#DC2626"></span> External API</span>
      <span class="legend-hint">Click an agent node to highlight its connections</span>
    </div>

    <div class="graph-scroll-wrapper">
      <svg [attr.width]="GRAPH_W" [attr.height]="GRAPH_H" class="graph-svg">

        <!-- ── Defs ── -->
        <defs>
          <marker id="arrow-pipeline" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L0,6 L6,3 z" fill="#1E40AF" opacity="0.6"/>
          </marker>
          <marker id="arrow-llm" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L0,6 L6,3 z" fill="#7C3AED" opacity="0.5"/>
          </marker>
          <marker id="arrow-api" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L0,6 L6,3 z" fill="#DC2626" opacity="0.5"/>
          </marker>
        </defs>

        <!-- ── Pipeline edges ── -->
        <g class="edges-pipeline">
          <line *ngFor="let e of pipelineEdges"
            [attr.x1]="nodeById(parsePipelineEdge(e).from)?.x"
            [attr.y1]="nodeById(parsePipelineEdge(e).from)?.y"
            [attr.x2]="nodeById(parsePipelineEdge(e).to)?.x"
            [attr.y2]="nodeById(parsePipelineEdge(e).to)?.y"
            [attr.stroke-opacity]="edgeHighlighted(parsePipelineEdge(e).from, parsePipelineEdge(e).to) ? 1 : (highlightedAgent ? 0.08 : 0.35)"
            [attr.stroke-width]="edgeHighlighted(parsePipelineEdge(e).from, parsePipelineEdge(e).to) ? 2.5 : 1.5"
            stroke="#1E40AF"
            marker-end="url(#arrow-pipeline)"/>
        </g>

        <!-- ── LLM edges ── -->
        <g class="edges-llm">
          <line *ngFor="let e of llmEdges"
            [attr.x1]="nodeById(e.from)?.x"
            [attr.y1]="nodeById(e.from)?.y"
            [attr.x2]="nodeById(e.to)?.x"
            [attr.y2]="nodeById(e.to)?.y"
            [attr.stroke-opacity]="edgeHighlighted(e.from, e.to) ? 0.85 : (highlightedAgent ? 0.05 : 0.18)"
            [attr.stroke-width]="edgeHighlighted(e.from, e.to) ? 2 : 1"
            stroke="#7C3AED"
            stroke-dasharray="4 3"
            marker-end="url(#arrow-llm)"/>
        </g>

        <!-- ── API edges ── -->
        <g class="edges-api">
          <line *ngFor="let e of apiEdges"
            [attr.x1]="nodeById(e.from)?.x"
            [attr.y1]="nodeById(e.from)?.y"
            [attr.x2]="nodeById(e.to)?.x"
            [attr.y2]="nodeById(e.to)?.y"
            [attr.stroke-opacity]="edgeHighlighted(e.from, e.to) ? 0.85 : (highlightedAgent ? 0.05 : 0.18)"
            [attr.stroke-width]="edgeHighlighted(e.from, e.to) ? 2 : 1"
            stroke="#DC2626"
            stroke-dasharray="3 4"
            marker-end="url(#arrow-api)"/>
        </g>

        <!-- ── LLM provider nodes ── -->
        <g *ngFor="let n of llmNodes"
           class="graph-node"
           [attr.opacity]="graphNodeOpacity(n.id)"
           (click)="clickGraphNode(n.id)"
           style="cursor:pointer">
          <circle [attr.cx]="n.x" [attr.cy]="n.y" r="28"
                  [attr.fill]="n.color + '22'" [attr.stroke]="n.color" stroke-width="2"/>
          <text [attr.x]="n.x" [attr.y]="n.y - 4" text-anchor="middle" font-size="14">🤖</text>
          <text [attr.x]="n.x" [attr.y]="n.y + 42" text-anchor="middle" font-size="10" fill="#CBD5E1">{{n.label}}</text>
        </g>

        <!-- ── API / service nodes ── -->
        <g *ngFor="let n of apiNodes"
           class="graph-node"
           [attr.opacity]="graphNodeOpacity(n.id)"
           (click)="clickGraphNode(n.id)"
           style="cursor:pointer">
          <rect [attr.x]="n.x - 28" [attr.y]="n.y - 20" width="56" height="40" rx="8"
                [attr.fill]="n.color + '22'" [attr.stroke]="n.color" stroke-width="2"/>
          <text [attr.x]="n.x" [attr.y]="n.y + 5" text-anchor="middle" font-size="14">{{n.icon}}</text>
          <text [attr.x]="n.x" [attr.y]="n.y + 30" text-anchor="middle" font-size="10" fill="#CBD5E1">{{n.label}}</text>
        </g>

        <!-- ── Agent nodes ── -->
        <g *ngFor="let n of agentNodes"
           class="graph-node"
           [attr.opacity]="graphNodeOpacity(n.id)"
           (click)="clickGraphNode(n.id)"
           style="cursor:pointer">
          <circle [attr.cx]="n.x" [attr.cy]="n.y"
                  [attr.r]="n.layer === 0 ? 38 : 28"
                  [attr.fill]="n.color + '33'" [attr.stroke]="n.color"
                  [attr.stroke-width]="highlightedAgent === n.id ? 3.5 : 2"
                  [attr.filter]="highlightedAgent === n.id ? 'url(#glow)' : ''"/>
          <text [attr.x]="n.x" [attr.y]="n.y + 5" text-anchor="middle"
                [attr.font-size]="n.layer === 0 ? 18 : 14"
                fill="#F1F5F9" font-family="Material Icons">{{n.icon}}</text>
          <text [attr.x]="n.x" [attr.y]="n.y + (n.layer === 0 ? 56 : 46)"
                text-anchor="middle" font-size="10" fill="#CBD5E1"
                [attr.font-weight]="highlightedAgent === n.id ? 700 : 400">
            {{n.id.replace('Agent', '')}}
          </text>
        </g>

      </svg>
    </div>

    <!-- Highlighted agent detail strip -->
    <div class="graph-detail-strip" *ngIf="highlightedAgent && selected">
      <mat-icon [style.color]="selected.color">{{selected.icon}}</mat-icon>
      <div>
        <strong>{{selected.name}}</strong> — {{selected.role}}
        <br>
        <span class="strip-desc">{{selected.description}}</span>
      </div>
      <button mat-stroked-button (click)="activeTab = 'catalog'">
        <mat-icon>open_in_new</mat-icon> Full Profile
      </button>
    </div>
  </div>

</div>
